import React, { useMemo, useRef, useState } from 'react';
import { Alert, Box, Button, Dialog, DialogContent, DialogTitle, TextField } from '@mui/material';
import { FileLibraryListItem, ReactMediaLibrary } from 'react-media-library';
import { IStoredFileDto } from '../../clients/services';

interface MediaPickerDialogProps {
    open: boolean;
    files: IStoredFileDto[];
    onClose: () => void;
    onPick: (file: IStoredFileDto) => void;
    onUpload?: (file: File) => Promise<boolean>;
    onRefresh?: () => Promise<void>;
}

const imageTypes = ['image/jpeg', 'image/png', 'image/gif', 'image/webp', 'image/svg+xml', 'image/avif'];
const acceptedLibraryTypes = [
    ...imageTypes,
    'application/pdf',
    'video/mp4',
    'video/webm',
    'video/quicktime',
    'audio/mpeg',
    'audio/wav',
    'audio/ogg',
];
const serviceUrl = (process.env.REACT_APP_SERVICE_URL as string | undefined)?.replace(/\/$/, '') ?? '';

const resolveFilePublicUrl = (file: IStoredFileDto): string => {
    const rawUrl = file.publicUrl ?? '';
    if (!rawUrl) {
        return '';
    }

    if (rawUrl.startsWith('http://') || rawUrl.startsWith('https://')) {
        return rawUrl;
    }

    const normalizedPath = rawUrl.startsWith('/') ? rawUrl : `/${rawUrl}`;
    const isManagedLibraryPath = normalizedPath.startsWith('/library/') || normalizedPath.startsWith('/files/');
    if (!isManagedLibraryPath) {
        return rawUrl;
    }

    if (!serviceUrl) {
        return rawUrl;
    }

    return `${serviceUrl}${normalizedPath}`;
};

const getFileDisplayPath = (file: IStoredFileDto): string => {
    const rawUrl = file.publicUrl ?? '';

    let normalizedPath = rawUrl;
    if (rawUrl.startsWith('http://') || rawUrl.startsWith('https://')) {
        try {
            normalizedPath = new URL(rawUrl).pathname;
        } catch {
            normalizedPath = rawUrl;
        }
    }

    if (normalizedPath.startsWith('/library/')) {
        return normalizedPath.slice(1);
    }

    if (normalizedPath.startsWith('/files/')) {
        return `library/${file.fileName}`;
    }

    if (normalizedPath.startsWith('library/')) {
        return normalizedPath;
    }

    if (normalizedPath.startsWith('files/')) {
        return `library/${file.fileName}`;
    }

    return file.fileName;
};

const normalizeSearchText = (value?: string): string => {
    return (value ?? '')
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '')
        .toLowerCase();
};

export const MediaPickerDialog: React.FC<MediaPickerDialogProps> = ({
    open,
    files,
    onClose,
    onPick,
    onUpload,
    onRefresh,
}) => {
    const [searchQuery, setSearchQuery] = useState('');
    const [appliedQuery, setAppliedQuery] = useState('');
    const [isUploading, setIsUploading] = useState(false);
    const [uploadError, setUploadError] = useState('');
    const fileInputRef = useRef<HTMLInputElement | null>(null);

    const filteredFiles = useMemo(() => {
        const normalizedQuery = normalizeSearchText(appliedQuery).trim();
        if (!normalizedQuery) {
            return files;
        }

        return files.filter((file) => {
            const haystack = normalizeSearchText(
                [file.fileName, file.originalName, file.title ?? '', file.comment ?? '', file.category ?? ''].join(' '),
            );
            return haystack.includes(normalizedQuery);
        });
    }, [files, appliedQuery]);

    const items = useMemo<FileLibraryListItem[]>(() => {
        return files
            .filter((file) => imageTypes.includes(file.mimeType))
            .filter((file) => filteredFiles.some((candidate) => candidate.id === file.id))
            .map((file) => ({
                _id: file.id,
                title: file.title || file.originalName,
                fileName: file.fileName,
                displayPath: getFileDisplayPath(file),
                createdAt: file.createdAt,
                size: file.size,
                thumbnailUrl: resolveFilePublicUrl(file),
                item: file,
            }));
    }, [files, filteredFiles]);

    const renderLibraryCard = (item: FileLibraryListItem) => {
        return (
            <div className="react-media-library__file-library-card">
                {item.thumbnailUrl && (
                    <img
                        className="react-media-library__file-library-card__image"
                        src={item.thumbnailUrl}
                        alt={item.fileName}
                    />
                )}
                <div className="react-media-library__file-library-card__path">{item.displayPath ?? item.fileName}</div>
            </div>
        );
    };

    const renderTopBar = () => {
        return (
            <Box
                sx={{
                    display: 'flex',
                    flexWrap: 'wrap',
                    gap: 1,
                    mb: 1.5,
                    alignItems: 'center',
                }}
            >
                <TextField
                    size="small"
                    label="Bilder suchen"
                    placeholder="Dateiname, Titel, Kommentar"
                    value={searchQuery}
                    onChange={(event) => setSearchQuery(event.target.value)}
                    onKeyDown={(event) => {
                        if (event.key === 'Enter') {
                            setAppliedQuery(searchQuery);
                        }
                    }}
                    sx={{ minWidth: 240, flex: '1 1 280px' }}
                />
                <Button variant="outlined" onClick={() => setAppliedQuery(searchQuery)}>
                    Suche anwenden
                </Button>
                {onUpload && (
                    <>
                        <Button variant="outlined" disabled={isUploading} onClick={() => fileInputRef.current?.click()}>
                            {isUploading ? 'Upload läuft...' : 'Datei wählen'}
                        </Button>
                        <input
                            ref={fileInputRef}
                            type="file"
                            hidden
                            accept="image/*,application/pdf,video/*,audio/*"
                            onChange={async (event) => {
                                const file = event.target.files?.[0];
                                event.currentTarget.value = '';

                                if (!file || !onUpload) {
                                    return;
                                }

                                setUploadError('');
                                setIsUploading(true);
                                try {
                                    const ok = await onUpload(file);
                                    if (!ok) {
                                        setUploadError('Upload fehlgeschlagen.');
                                    } else if (onRefresh) {
                                        await onRefresh();
                                    }
                                } catch {
                                    setUploadError('Upload fehlgeschlagen.');
                                } finally {
                                    setIsUploading(false);
                                }
                            }}
                        />
                    </>
                )}
            </Box>
        );
    };

    return (
        <Dialog open={open} onClose={onClose} fullWidth maxWidth="lg">
            <DialogTitle>Bild aus Medienbibliothek auswählen</DialogTitle>
            <DialogContent>
                <ReactMediaLibrary
                    isOpen
                    onClose={onClose}
                    modalTitle="SwissTexMusic Medien"
                    fileLibraryList={items}
                    sortProperty="createdAt"
                    sortAscending={false}
                    libraryCardComponent={renderLibraryCard}
                    multiSelect={false}
                    topBarComponent={renderTopBar}
                    acceptedTypes={acceptedLibraryTypes}
                    filesSelectCallback={(selected) => {
                        const first = selected[0]?.item as IStoredFileDto | undefined;
                        if (first) {
                            onPick(first);
                        }
                        onClose();
                    }}
                />
                {uploadError && (
                    <Alert severity="error" sx={{ mt: 1 }}>
                        {uploadError}
                    </Alert>
                )}
            </DialogContent>
        </Dialog>
    );
};
